import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTheme } from "@mui/material";
import { useClinicalInsightsFilters } from "src-next/modules/clinical-insights/hooks/useClinicalInsightsFilters";
import { useScrollParentHeight } from "./hooks/useScrollParentHeight";
import type { LoadPhase } from "src-next/modules/clinical-insights/loadPhase";
import {
  type AnalysisCardId,
  WORKSPACE_ANALYSES,
} from "src-next/modules/clinical-insights/types/analysisTypes";
import { ClinicalPractitionerSelect } from "src-next/modules/clinical-insights/components/page/ClinicalPractitionerSelect";
import { CompactPeriodControl } from "src-next/modules/clinical-insights/components/page/CompactPeriodControl";
import { getInsightAnalysisTexts } from "src-next/modules/clinical-insights/components/workspace/config/analysisInsightConfig";
import { getAnalysisAccentColor } from "src-next/modules/clinical-insights/constants/clinicalInsightsTheme";
import { isVetApp } from "src-next/modules/crm-clinical/messaging/helpers/isVetApp";
import { smoothScrollElement } from "src-next/modules/clinical-insights/helpers/smoothScrollElement";
import {
  ANALYSIS_QUERY_PARAM,
  DEFAULT_ANALYSIS_ID,
  isAnalysisCardId,
} from "./analysisPageConstants";
import { useStableAnalysisLoadPhaseCallbacks } from "./useStableAnalysisLoadPhaseCallbacks";

export function useClinicalInsightsPageState() {
  const theme = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = useClinicalInsightsFilters();
  const [patientStats, setPatientStats] = useState({ filtered: 0, total: 0 });
  const [workspaceLoadPhase, setWorkspaceLoadPhase] = useState<LoadPhase>("idle");
  const frameRef = useRef<HTMLDivElement>(null);
  const [frameEl, setFrameEl] = useState<HTMLDivElement | null>(null);
  const scrollParentContentHeight = useScrollParentHeight(frameEl);

  const selectedAnalysisId = useMemo((): AnalysisCardId => {
    const fromUrl = searchParams.get(ANALYSIS_QUERY_PARAM);
    if (isVetApp() && fromUrl === "sem-atendimento") return DEFAULT_ANALYSIS_ID;
    return isAnalysisCardId(fromUrl) ? fromUrl : DEFAULT_ANALYSIS_ID;
  }, [searchParams]);

  const selectedAccentColor = useMemo(
    () => getAnalysisAccentColor(theme, selectedAnalysisId),
    [theme, selectedAnalysisId]
  );

  const [visitedAnalysisIds, setVisitedAnalysisIds] = useState<Set<AnalysisCardId>>(
    () => new Set([selectedAnalysisId])
  );

  const selectedAnalysisIdRef = useRef(selectedAnalysisId);
  selectedAnalysisIdRef.current = selectedAnalysisId;
  const pendingFrameScrollRef = useRef(false);

  const setSelectedAnalysisId = useCallback(
    (id: AnalysisCardId) => {
      if (id === selectedAnalysisIdRef.current) return;
      pendingFrameScrollRef.current = true;
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set(ANALYSIS_QUERY_PARAM, id);
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  useEffect(() => {
    if (!pendingFrameScrollRef.current) return;
    pendingFrameScrollRef.current = false;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        smoothScrollElement(frameRef.current, {
          align: "frameTop",
          offsetPx: 8,
          durationMs: 1000,
        });
      });
    });
  }, [selectedAnalysisId]);

  useEffect(() => {
    const fromUrl = searchParams.get(ANALYSIS_QUERY_PARAM);
    const blockedInVet = isVetApp() && fromUrl === "sem-atendimento";
    if (!isAnalysisCardId(fromUrl) || blockedInVet) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set(ANALYSIS_QUERY_PARAM, DEFAULT_ANALYSIS_ID);
          return next;
        },
        { replace: true }
      );
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    setVisitedAnalysisIds((prev) => {
      if (prev.has(selectedAnalysisId)) return prev;
      const next = new Set(prev);
      next.add(selectedAnalysisId);
      return next;
    });
    setPatientStats({ filtered: 0, total: 0 });
  }, [selectedAnalysisId]);

  const handlePatientStatsChange = useCallback((filtered: number, total: number) => {
    setPatientStats({ filtered, total });
  }, []);

  const sharedInsightProps = useMemo(
    () => ({
      dateRange: filters.appliedDateRange,
      practitionerId: filters.practitionerId,
      practitioner: filters.practitioner,
    }),
    [filters.appliedDateRange, filters.practitionerId, filters.practitioner]
  );

  const practitionerFilterNode = useMemo(
    () => (
      <ClinicalPractitionerSelect
        fullWidth
        showFloatingLabel={false}
        practitionerId={filters.practitionerId}
        onPractitionerChange={filters.handlePractitionerChange}
        practitioner={filters.practitioner}
      />
    ),
    [filters.practitionerId, filters.handlePractitionerChange, filters.practitioner]
  );

  const showPractitionerInTopBar =
    !WORKSPACE_ANALYSES.has(selectedAnalysisId) &&
    selectedAnalysisId !== "sem-atendimento";

  const periodDisplayLabel = useMemo(() => {
    const d = filters.selectedDate;
    if (filters.dateFilterType === "month") {
      return d.format("MMMM [de] YYYY");
    }
    if (filters.dateFilterType === "week") {
      return `semana de ${d.format("DD/MM/YYYY")}`;
    }
    return d.format("DD/MM/YYYY");
  }, [filters.selectedDate, filters.dateFilterType]);

  const practitionerBusyLabel = useMemo(() => {
    if (filters.practitionerId === "all") return "todos os profissionais";
    const entry = filters.practitioner?.find(
      (p) => p.resource?.id === filters.practitionerId
    );
    const name = entry?.resource?.name?.[0]?.text;
    return name || "profissional selecionado";
  }, [filters.practitioner, filters.practitionerId]);

  const workspaceBusy =
    WORKSPACE_ANALYSES.has(selectedAnalysisId) &&
    (workspaceLoadPhase === "records" || workspaceLoadPhase === "patients");
  const isBusy = filters.isApplying || workspaceBusy;

  const busyMetaText = useMemo(() => {
    if (!isBusy || !WORKSPACE_ANALYSES.has(selectedAnalysisId)) return undefined;
    const verb = getInsightAnalysisTexts(selectedAnalysisId).busyVerb;
    return `${verb} · ${periodDisplayLabel} · ${practitionerBusyLabel}`;
  }, [isBusy, selectedAnalysisId, periodDisplayLabel, practitionerBusyLabel]);

  const framePeriodControl = useMemo(
    () => (
      <CompactPeriodControl
        selectedDate={filters.selectedDate}
        dateFilterType={filters.dateFilterType}
        label={periodDisplayLabel}
        accentColor={selectedAccentColor}
        onDateChange={filters.handleDateChange}
        onFilterTypeChange={filters.handleFilterTypeChange}
        onResetPeriod={filters.resetPeriod}
        disabled={false}
      />
    ),
    [
      filters.selectedDate,
      filters.dateFilterType,
      filters.handleDateChange,
      filters.handleFilterTypeChange,
      filters.resetPeriod,
      periodDisplayLabel,
      selectedAccentColor,
    ]
  );

  const handleWorkspaceLoadPhase = useCallback((phase: LoadPhase) => {
    setWorkspaceLoadPhase(phase);
  }, []);

  const loadPhaseCallbacks = useStableAnalysisLoadPhaseCallbacks(
    selectedAnalysisIdRef,
    handleWorkspaceLoadPhase
  );

  const handlePreviousPeriod = useCallback(() => {
    filters.handleDateChange(filters.selectedDate.subtract(1, "month"));
    filters.handleFilterTypeChange("month");
  }, [filters]);

  const handleSelectAllPractitioners = useCallback(() => {
    filters.handlePractitionerChange("");
  }, [filters]);

  const workspaceInsightProps = useMemo(
    () => ({
      onPatientStatsChange: handlePatientStatsChange,
      practitionerFilter: practitionerFilterNode,
      onSelectAllPractitioners: handleSelectAllPractitioners,
      onPreviousPeriod: handlePreviousPeriod,
      periodDisplayLabel,
    }),
    [
      handlePatientStatsChange,
      practitionerFilterNode,
      handleSelectAllPractitioners,
      handlePreviousPeriod,
      periodDisplayLabel,
    ]
  );

  const setFrameRef = useCallback(
    (node: HTMLDivElement | null) => {
      frameRef.current = node;
      setFrameEl(node);
    },
    []
  );

  return {
    theme,
    filters,
    selectedAnalysisId,
    selectedAccentColor,
    visitedAnalysisIds,
    setSelectedAnalysisId,
    patientStats,
    isBusy,
    busyMetaText,
    framePeriodControl,
    scrollParentContentHeight,
    showPractitionerInTopBar,
    sharedInsightProps,
    workspaceInsightProps,
    selectedAnalysisIdRef,
    handleWorkspaceLoadPhase,
    loadPhaseCallbacks,
    setFrameRef,
  };
}
